import React, { useEffect, useRef, useState } from "react";
import { openerHoldMs } from "@hudhud/core";
import { activeCard, WIDTH } from "@hudhud/reel";
import { Icon } from "../components/Icon.js";
import { useEditor } from "./store.js";
import { usePhone } from "../hooks/useMedia.js";
import { labelFor, hasText, sizeOf, withSize, settingsFor, snapshot, placed, clamp, type Kind, type Box } from "./editKinds.js";

/**
 * Direct editing on the video. Everything the picture draws with a data-edit mark gets a frame here: tap to select it, drag to move
 * it, drag the corner dot to resize it, tap the pencil (or tap twice) to retype its text, pinch the video to zoom the framing.
 * What else can be done to the selected thing sits in the row under the preview, never over the picture.
 */
export const EditOverlay: React.FC<{ width: number; height: number; playerRoot: React.RefObject<HTMLDivElement | null> }> = ({ width, height, playerRoot }) => {
  const clip = useEditor((s) => s.clip);
  const playheadMs = useEditor((s) => s.playheadMs);
  const playing = useEditor((s) => s.playing);
  const selected = useEditor((s) => s.editSel) as Kind | null;
  const textEditRequest = useEditor((s) => s.textEditRequest);
  const { update, setPlaying, setEditSel, setInspectorTab } = useEditor.getState();
  const phone = usePhone();
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [hover, setHover] = useState<Kind | null>(null);
  const [dragging, setDragging] = useState<Kind | null>(null);
  const [guide, setGuide] = useState(false);
  const [editing, setEditing] = useState<{ kind: Kind; box: Box } | null>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ d0: number; z0: number } | null>(null);
  const lastTap = useRef<{ kind: Kind; at: number } | null>(null);
  const scale = WIDTH / Math.max(1, width);

  // find the editable blocks the picture drew: on every pause/seek, and only now and then while it plays (layout reads are not free)
  useEffect(() => {
    let alive = true;
    const scan = () => {
      const root = playerRoot.current;
      if (!root || !alive) return;
      const frame = root.getBoundingClientRect();
      const best = new Map<Kind, Box>();
      root.querySelectorAll<HTMLElement>("[data-edit]").forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.width < 2 || r.height < 2) return;
        const kind = el.dataset.edit as Kind;
        const box = { kind, x: r.left - frame.left, y: r.top - frame.top, w: Math.min(r.width, width), h: Math.min(r.height, height) };
        if (kind === "video") { box.x = 0; box.y = 0; box.w = width; box.h = height; }
        const have = best.get(kind);
        if (!have || box.w * box.h > have.w * have.h) best.set(kind, box);
      });
      const found = [...best.values()].sort((a, b) => (a.kind === "video" ? -1 : b.kind === "video" ? 1 : 0));
      setBoxes((prev) => (JSON.stringify(prev) === JSON.stringify(found) ? prev : found));
    };
    if (playing) { const t = setInterval(scan, 600); return () => { alive = false; clearInterval(t); }; }
    scan();
    let pending: ReturnType<typeof setTimeout> | null = null;
    const mo = new MutationObserver(() => { if (pending) return; pending = setTimeout(() => { pending = null; scan(); }, 60); });
    if (playerRoot.current) mo.observe(playerRoot.current, { childList: true, subtree: true, attributes: true, attributeFilter: ["style", "data-edit"] });
    const late = setTimeout(scan, 700);
    return () => { alive = false; mo.disconnect(); if (pending) clearTimeout(pending); clearTimeout(late); };
  }, [playerRoot, playheadMs, clip, width, height, playing]);

  // the row under the preview asked for the text editor
  useEffect(() => {
    if (!textEditRequest || !selected || !hasText(selected)) return;
    const b = boxes.find((x) => x.kind === selected);
    if (b) { setPlaying(false); setEditing({ kind: selected, box: b }); }
  }, [textEditRequest]);
  // the selected thing left the picture (hidden, or the moment passed): let go quietly
  useEffect(() => { if (selected && selected !== "video" && boxes.length && !boxes.some((b) => b.kind === selected) && !editing) setEditSel(null); }, [boxes]);

  // keys while something is selected: Enter edits, Escape lets go, arrows nudge (Shift = bigger steps)
  useEffect(() => {
    if (!selected) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable || editing) return;
      const c = useEditor.getState().clip;
      if (!c) return;
      if (e.key === "Escape") { setEditSel(null); e.stopImmediatePropagation(); return; }
      if (e.key === "Enter" && hasText(selected)) { const b = boxes.find((x) => x.kind === selected); if (b) { setPlaying(false); setEditing({ kind: selected, box: b }); } e.preventDefault(); e.stopImmediatePropagation(); return; }
      const step = e.shiftKey ? 20 : 4;
      const d: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
      if (d[e.key]) { const [dx, dy] = d[e.key]; update((cur) => placed(cur, selected, snapshot(cur), dx, dy)); e.preventDefault(); e.stopImmediatePropagation(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [selected, editing, boxes]);

  if (!clip) return null;
  const hold = openerHoldMs(clip);
  const dist = () => { const [a, b] = [...pointers.current.values()]; return Math.hypot(a.x - b.x, a.y - b.y); };

  const startDrag = (e: React.PointerEvent, box: Box) => {
    if (editing) return;
    e.preventDefault(); e.stopPropagation();
    const kind = box.kind;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    // a second finger on the video: pinch to zoom the framing
    if (kind === "video" && pointers.current.size === 2) { pinch.current = { d0: Math.max(1, dist()), z0: useEditor.getState().clip!.frameZoom ?? 1 }; return; }
    setPlaying(false);
    const start = { x: e.clientX, y: e.clientY, snap: snapshot(useEditor.getState().clip!) };
    let moved = false;
    const move = (ev: PointerEvent) => {
      if (pointers.current.has(ev.pointerId)) pointers.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      if (pinch.current && pointers.current.size >= 2) { const z = clamp(pinch.current.z0 * (dist() / pinch.current.d0), 1, 2.5); update((c) => ({ ...c, frameZoom: Math.round(z * 100) / 100 }), { transient: true }); return; }
      if (ev.pointerId !== e.pointerId) return;
      const dx = (ev.clientX - start.x) * scale, dy = (ev.clientY - start.y) * scale;
      if (!moved && Math.abs(dx) + Math.abs(dy) < 6 * scale) return;
      moved = true; setDragging(kind);
      update((c) => placed(c, kind, start.snap, dx, dy), { transient: true });
      if (kind === "captions") setGuide(useEditor.getState().clip!.style.xOffset === 0);
    };
    const up = (ev: PointerEvent) => {
      pointers.current.delete(ev.pointerId);
      if (pointers.current.size < 2 && pinch.current) { pinch.current = null; update((c) => c); }
      if (ev.pointerId !== e.pointerId) return;
      window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); window.removeEventListener("pointercancel", up);
      setDragging(null); setGuide(false);
      if (moved) { update((c) => c); setEditSel(kind); return; }
      // a tap: select it; a second tap on the same thing within a moment opens the text
      const now = Date.now();
      const last = lastTap.current;
      if (last && last.kind === kind && now - last.at < 380 && hasText(kind)) { lastTap.current = null; setEditSel(kind); setEditing({ kind, box }); return; }
      lastTap.current = { kind, at: now };
      setEditSel(kind);
    };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up); window.addEventListener("pointercancel", up);
  };

  /** the corner dot: drag it out to make the thing bigger */
  const startResize = (e: React.PointerEvent, box: Box) => {
    e.preventDefault(); e.stopPropagation();
    const kind = box.kind;
    const s0 = sizeOf(useEditor.getState().clip!, kind);
    if (s0 == null) return;
    const x0 = e.clientX, y0 = e.clientY;
    const base = Math.max(40, Math.max(box.w, box.h));
    const move = (ev: PointerEvent) => { const d = (ev.clientX - x0 + ev.clientY - y0) / 2; update((c) => withSize(c, kind, s0 * (1 + d / base)), { transient: true }); };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); window.removeEventListener("pointercancel", up); setDragging(null); update((c) => c); };
    setDragging(kind);
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up); window.addEventListener("pointercancel", up);
  };

  const textFor = (kind: Kind): string => {
    if (kind === "opener") return clip.hook.text;
    if (kind === "endcard") return clip.endCard.line;
    if (kind === "badge") return clip.hook.badge;
    if (kind.startsWith("behind:")) return clip.behindTexts.find((t) => t.id === kind.slice(7))?.text ?? "";
    if (kind === "captions") { const hit = activeCard(clip.captions, playheadMs - hold); return hit ? hit.card.words.map((w) => w.text).join(" ") : ""; }
    return "";
  };
  const commit = (kind: Kind, text: string) => {
    // captions are timed word by word, so they stay on one line; the opener, the end card and the words behind the speaker keep their line breaks
    const value = kind === "captions" ? text.replace(/\s+/g, " ").trim() : text.replace(/[ \t]+/g, " ").replace(/ *\n */g, "\n").replace(/\n{3,}/g, "\n\n").trim();
    setEditing(null);
    if (kind === "opener") { if (value !== clip.hook.text) update((c) => ({ ...c, hook: { ...c.hook, text: value } })); return; }
    if (kind === "endcard") { if (value !== clip.endCard.line) update((c) => ({ ...c, endCard: { ...c.endCard, line: value } })); return; }
    if (kind === "badge") { if (value !== clip.hook.badge) update((c) => ({ ...c, hook: { ...c.hook, badge: value } })); return; }
    if (kind.startsWith("behind:")) { const id = kind.slice(7); update((c) => ({ ...c, behindTexts: c.behindTexts.map((t) => (t.id === id ? { ...t, text: value } : t)) })); return; }
    if (kind === "captions") {
      const hit = activeCard(clip.captions, playheadMs - hold);
      if (!hit) return;
      const tokens = value.split(" ").filter(Boolean);
      // the card's words are timeline copies; find the source words that produced them by matching text in order
      const src = useEditor.getState().clip!;
      const cardTexts = hit.card.words.map((w) => w.text);
      const sourceWords = src.words.filter((w) => w.endMs > src.edits!.trimStartMs && w.startMs < src.edits!.trimEndMs);
      let startIdx = -1;
      for (let i = 0; i + cardTexts.length <= sourceWords.length; i++) {
        if (cardTexts.every((t, k) => (src.edits!.wordText[String(sourceWords[i + k].startMs)] ?? sourceWords[i + k].text) === t)) { startIdx = i; break; }
      }
      if (startIdx < 0) return;
      const targets = sourceWords.slice(startIdx, startIdx + cardTexts.length);
      const wordText = { ...src.edits!.wordText };
      targets.forEach((w, k) => {
        const t = k < targets.length - 1 ? tokens[k] ?? "" : tokens.slice(k).join(" ");
        if (t === w.text) delete wordText[String(w.startMs)]; else wordText[String(w.startMs)] = t;
      });
      update((c) => ({ ...c, edits: { ...c.edits!, wordText } }));
    }
  };

  const pad = phone ? 10 : 6;
  const corner = phone ? 40 : 24;
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      {guide ? <div className="edit-guide" /> : null}
      {boxes.map((b) => {
        const isSel = selected === b.kind;
        const on = hover === b.kind || dragging === b.kind;
        const isVideo = b.kind === "video";
        const size = isSel ? sizeOf(clip, b.kind) : null;
        return (
          <div key={b.kind} className="edit-frame" role="button" tabIndex={-1} aria-label={labelFor(b.kind)} aria-pressed={isSel} onPointerEnter={() => setHover(b.kind)} onPointerLeave={() => setHover((h) => (h === b.kind ? null : h))} onPointerDown={(e) => startDrag(e, b)}
            style={{ left: isVideo ? 0 : b.x - pad, top: isVideo ? 0 : b.y - pad, width: isVideo ? b.w : b.w + pad * 2, height: isVideo ? b.h : b.h + pad * 2, cursor: dragging === b.kind ? "grabbing" : isVideo ? "move" : "grab", borderRadius: isVideo ? 0 : 8,
              border: isSel && !isVideo ? "2px solid var(--gold)" : on && !isVideo ? "1.5px dashed var(--gold)" : "1.5px dashed transparent", background: on && !isVideo && !isSel ? "rgba(245,197,66,0.06)" : "transparent", boxShadow: isSel && isVideo ? "inset 0 0 0 3px var(--gold)" : undefined }}
            title={isVideo ? "Drag to reframe the video · pinch or use the row below to zoom" : hasText(b.kind) ? "Drag to move · tap twice to edit the text" : `Drag to move the ${labelFor(b.kind).toLowerCase()}`}>
            {on && !isSel && !editing && !phone && !isVideo ? <span className="edit-tag">{labelFor(b.kind)}</span> : null}
            {isSel && !editing && !isVideo ? <>
              <button className="edit-corner edit" style={{ width: corner, height: corner, right: -corner / 2, top: -corner / 2 }} aria-label={hasText(b.kind) ? "Edit the text" : "Open its settings"} title={hasText(b.kind) ? "Edit the text" : "Settings"}
                onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); if (hasText(b.kind)) { setPlaying(false); setEditing({ kind: b.kind, box: b }); } else { const [tab, section] = settingsFor(b.kind); if (b.kind.startsWith("overlay:")) useEditor.getState().setSelectedOverlay(b.kind.slice(8)); setInspectorTab(tab, section); } }}><Icon name={hasText(b.kind) ? "type" : "settings"} size={phone ? 18 : 12} /></button>
              {size != null ? <div className="edit-corner resize" style={{ width: corner, height: corner, right: -corner / 2, bottom: -corner / 2 }} onPointerDown={(e) => startResize(e, b)} title="Drag to resize" aria-label="Resize" role="button"><Icon name="fit" size={phone ? 18 : 12} /></div> : null}
            </> : null}
          </div>
        );
      })}
      {editing ? <InlineEditor box={editing.box} kind={editing.kind} initial={textFor(editing.kind)} phone={phone} frameW={width} frameH={height} onCommit={(t) => commit(editing.kind, t)} onCancel={() => setEditing(null)} /> : null}
    </div>
  );
};

/**
 * The text editor: on the desk it sits on the thing itself; on a phone it takes the bottom of the preview with finger-sized buttons.
 * Enter, Keep, a tap anywhere else, or the keyboard going away all keep the text and close it; Escape and Cancel let it go.
 */
const InlineEditor: React.FC<{ box: Box; kind: Kind; initial: string; phone: boolean; frameW: number; frameH: number; onCommit: (t: string) => void; onCancel: () => void }> = ({ box, kind, initial, phone, frameW, onCommit, onCancel }) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const [v, setV] = useState(initial);
  const latest = useRef(initial);
  const settled = useRef(false);
  const keep = () => { if (settled.current) return; settled.current = true; onCommit(latest.current); };
  const drop = () => { if (settled.current) return; settled.current = true; onCancel(); };
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  // a press anywhere outside the editor keeps the text: seen before the press does whatever else it does
  useEffect(() => {
    const onDown = (e: PointerEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) keep(); };
    window.addEventListener("pointerdown", onDown, true);
    return () => window.removeEventListener("pointerdown", onDown, true);
  }, []);
  const lines = kind !== "captions";
  const hint = kind === "captions" ? "the same number of words keeps the timing · Enter keeps" : `Enter for a new line${kind === "opener" || kind.startsWith("behind:") ? " · *stars* colour a word" : ""} · tap away to keep`;
  const pos: React.CSSProperties = phone ? { left: 8, right: 8, bottom: 8 } : { left: Math.max(4, Math.min(box.x - 4, frameW - 200)), top: Math.max(4, box.y - 4), width: Math.max(220, box.w + 8), maxWidth: "calc(100% - 8px)" };
  return (
    <div ref={wrap} className={`edit-text${phone ? " phone" : ""}`} style={{ position: "absolute", ...pos }}>
      <span className="lbl">{labelFor(kind)}</span>
      <textarea ref={ref} value={v} aria-label={`Edit the ${labelFor(kind).toLowerCase()}`} onChange={(e) => { setV(e.target.value); latest.current = e.target.value; }} rows={kind === "captions" ? 2 : 3} enterKeyHint={lines ? "enter" : "done"}
        onKeyDown={(e) => { if (e.key === "Enter" && (!lines || e.metaKey || e.ctrlKey)) { e.preventDefault(); keep(); } if (e.key === "Escape") drop(); }}
        onBlur={(e) => { if (!wrap.current?.contains(e.relatedTarget as Node | null)) keep(); }} />
      <div className="row" style={{ gap: 8, alignItems: "center" }}>
        <button className="btn sm gold" onPointerDown={(e) => { e.preventDefault(); keep(); }} onClick={keep}><Icon name="check" size={14} />Keep</button>
        <button className="btn sm" onPointerDown={(e) => { e.preventDefault(); drop(); }} onClick={drop}>Cancel</button>
        {hint ? <span className="hint" style={{ marginLeft: "auto", fontSize: 11 }}>{hint}</span> : null}
      </div>
    </div>
  );
};
